import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { Alert, Button, CircularProgress, Stack } from '@mui/material';

import {
  useGetDestinationContentTags,
  useReorderDestinationContentTags,
} from '~/hooks/apis/tags/useDestinationContentTagOrder';

import type { DestinationContentTag } from '~/services/TagsService';

import SortableDestinationContentTag from './SortableDestinationContentTag';

type Props = { destinationId: string; destinationName: string };

export default function DestinationContentTagOrderEditor({ destinationId, destinationName }: Props) {
  const { data: tags = [], error, isLoading, isSuccess } = useGetDestinationContentTags(destinationId);
  const reorderMutation = useReorderDestinationContentTags();
  const [orderedTags, setOrderedTags] = useState<Array<DestinationContentTag>>([]);
  const initializedDestinationId = useRef<string | undefined>(undefined);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  useEffect(() => {
    if (isSuccess && initializedDestinationId.current !== destinationId) {
      setOrderedTags(tags);
      initializedDestinationId.current = destinationId;
    }
  }, [destinationId, isSuccess, tags]);
  const isDirty = orderedTags.some((tag, index) => tag.id !== tags[index]?.id);
  const moveTag = useCallback(
    (tagId: string, direction: 'up' | 'down') =>
      setOrderedTags((current) => {
        const index = current.findIndex((tag) => tag.id === tagId);
        const nextIndex = direction === 'up' ? index - 1 : index + 1;
        return index < 0 || nextIndex < 0 || nextIndex >= current.length
          ? current
          : arrayMove(current, index, nextIndex);
      }),
    [],
  );
  const handleDragEnd = useCallback(({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setOrderedTags((current) => {
      const oldIndex = current.findIndex((tag) => tag.id === active.id);
      const newIndex = current.findIndex((tag) => tag.id === over.id);
      return oldIndex < 0 || newIndex < 0 ? current : arrayMove(current, oldIndex, newIndex);
    });
  }, []);
  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Tags Ranking — {destinationName}. Drag tags or use the arrow controls, then save the full order.
      </Alert>
      {isLoading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error instanceof Error ? error.message : 'Failed to load Content Tags'}</Alert>
      ) : orderedTags.length === 0 ? (
        <Alert severity="info">No permanent Content Tags are assigned to this location.</Alert>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
          <SortableContext items={orderedTags.map((tag) => tag.id)} strategy={verticalListSortingStrategy}>
            <Stack spacing={1}>
              {orderedTags.map((tag, index) => (
                <SortableDestinationContentTag
                  index={index}
                  key={tag.id}
                  onMove={moveTag}
                  tag={tag}
                  total={orderedTags.length}
                />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      )}
      <Stack direction="row" spacing={1}>
        <Button disabled={!isDirty || reorderMutation.isPending} onClick={() => setOrderedTags(tags)}>
          Discard changes
        </Button>
        <Button
          disabled={!isDirty || reorderMutation.isPending}
          onClick={() => reorderMutation.mutate({ destinationId, orderedTagIds: orderedTags.map((tag) => tag.id) })}
          variant="contained"
        >
          {reorderMutation.isPending ? 'Saving…' : 'Save order'}
        </Button>
      </Stack>
    </Stack>
  );
}
